import type { ScheduleModel } from '@/shared/generated'

import React, { useMemo } from 'react'
import { format, isAfter } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { formatSchedule } from '@/entities/optimization-schedule'

import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/shared/components/ui/hover-card'
import { TimezoneCard } from '@/shared/components/ui/relative-time-card'
import { Separator } from '@/shared/components/ui/separator'
import { getLangCode, useLocale } from '@/shared/i18n'
import { cn } from '@/shared/lib/utils'
import { utcToZonedTime } from '@/shared/tools/dates'

type ScheduleDate = {
	className?: string
	schedule: ScheduleModel
}
export const OptimizationScheduleDate = ({
	className,
	schedule,
}: ScheduleDate) => {
	const { t } = useTranslation()
	const currentLocale = useLocale()

	const value = new Date(schedule.nearest_date)
	const isStopped = useMemo(
		() => isAfter(new Date(), new Date(schedule.nearest_date)),
		[schedule.nearest_date]
	)

	return (
		<HoverCard>
			<HoverCardTrigger>
				<span
					className={cn(
						className,
						'flex items-center gap-1',
						isStopped && 'text-muted-foreground line-through'
					)}
				>
					{t('on')} {format(utcToZonedTime(value, 'UTC'), 'MMM dd')}
				</span>
			</HoverCardTrigger>
			<HoverCardContent className='w-auto p-0'>
				<div className='p-2 text-xs whitespace-break-spaces text-muted-foreground'>{`"${schedule.name}"\n${formatSchedule(schedule, t)}`}</div>
				<Separator />
				<div role='list' className='flex flex-col gap-1 p-2'>
					{['UTC'].map((timezone) => (
						<TimezoneCard
							key={timezone}
							role='listitem'
							locale={getLangCode(currentLocale)}
							date={value}
							timezone={timezone}
						/>
					))}
					<TimezoneCard
						locale={getLangCode(currentLocale)}
						role='listitem'
						date={value}
					/>
				</div>
			</HoverCardContent>
		</HoverCard>
	)
}
